import React, { useEffect, useMemo, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DeviceHubOutlinedIcon from '@mui/icons-material/DeviceHubOutlined';
import { CircularProgress } from '@mui/material';

type HierarchyEntity = {
  id?: string;
  name?: string;
  parent_id?: string | null;
  entity_type?: string;
  job_title?: string;
  category?: string;
  subcategory?: string;
  nodeType?: 'entity' | 'group';
  relationshipMeta?: string;
  relationshipId?: string;
};

type HierarchyNode = Omit<HierarchyEntity, 'id'> & {
  id: string;
  children: HierarchyNode[];
};

type RelationshipEdge = {
  id?: string;
  source_entity_id?: string;
  target_entity_id?: string;
  source_entity_name?: string;
  target_entity_name?: string;
  relationship_type?: string;
};

type RelationshipHierarchyPanelProps = {
  rootEntity: HierarchyEntity | null | undefined;
  hierarchyData?: { above?: HierarchyEntity[]; below?: HierarchyEntity[] };
  relationshipEdges?: RelationshipEdge[];
  isLoadingHierarchy: boolean;
  hierarchyError?: unknown;
  selectedEntityId?: string | null;
  showRelationships: boolean;
  onAddRelationship: () => void;
  onSelectEntity: (entityId: string) => void;
  onRemoveRelationship: (
    relationshipId: string,
    relatedEntityId: string,
    relatedEntityName?: string,
  ) => void;
  deletingRelationshipId?: string | null;
};

const formatRelationshipLabel = (value?: string) => {
  if (!value) {
    return '';
  }
  const acronymLabels: Record<string, string> = {
    ceo: 'CEO',
    cfo: 'CFO',
    coo: 'COO',
    cto: 'CTO',
  };
  return value
    .split('_')
    .map((word) => {
      const normalizedWord = word.toLowerCase();
      return (
        acronymLabels[normalizedWord] ||
        normalizedWord.charAt(0).toUpperCase() + normalizedWord.slice(1)
      );
    })
    .join(' ');
};

const invertRelationshipType = (value?: string) => {
  if (!value) {
    return value;
  }
  const inverseMap: Record<string, string> = {
    parent_company: 'subsidiary',
    subsidiary: 'parent_company',
    division: 'parent_company',
    branch_office: 'parent_company',
    franchise: 'parent_company',
  };
  return inverseMap[value] ?? value;
};

const getEntityMeta = (entity: HierarchyEntity) => {
  if (entity.nodeType === 'group') {
    return '';
  }
  if (entity.relationshipMeta) {
    return formatRelationshipLabel(entity.relationshipMeta);
  }
  if (entity.entity_type === 'person') {
    return entity.job_title ? `Person - ${entity.job_title}` : 'Person';
  }
  if (entity.entity_type) {
    return entity.entity_type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  const parts = [entity.category, entity.subcategory].filter(Boolean);
  return parts.length > 0 ? parts.join(' - ') : 'Organization';
};

const getEntityIcon = (entity: HierarchyEntity) => {
  if (entity.nodeType === 'group') {
    if (entity.name?.toLowerCase().includes('people')) {
      return <PersonOutlineOutlinedIcon fontSize='small' />;
    }
    return <BusinessOutlinedIcon fontSize='small' />;
  }
  if (entity.entity_type === 'person') {
    return <PersonOutlineOutlinedIcon fontSize='small' />;
  }
  if (entity.entity_type === 'location') {
    return <LocationOnOutlinedIcon fontSize='small' />;
  }
  if (entity.entity_type && entity.entity_type !== 'organization') {
    return <DeviceHubOutlinedIcon fontSize='small' />;
  }
  return <BusinessOutlinedIcon fontSize='small' />;
};

const buildHierarchyTree = (
  rootEntity?: HierarchyEntity | null,
  hierarchyData?: { above?: HierarchyEntity[]; below?: HierarchyEntity[] },
  relationshipEdges?: RelationshipEdge[],
): HierarchyNode | null => {
  if (!rootEntity?.id) {
    return null;
  }

  const rootId = rootEntity.id as string;
  const nodes = new Map<string, HierarchyNode>();

  nodes.set(rootId, {
    ...rootEntity,
    id: rootId,
    parent_id: undefined,
    nodeType: rootEntity?.nodeType ?? 'entity',
    children: [],
  });

  const addNode = (
    entity?: HierarchyEntity | null,
    overrides?: HierarchyEntity,
  ) => {
    const id = entity?.id;
    if (!id) {
      return;
    }
    if (!nodes.has(id)) {
      nodes.set(id, {
        ...entity,
        ...overrides,
        id: id,
        nodeType: overrides?.nodeType ?? entity?.nodeType ?? 'entity',
        relationshipId: overrides?.relationshipId ?? entity?.relationshipId,
        children: [],
      });
    } else {
      nodes.set(id, {
        ...nodes.get(id),
        ...entity,
        ...overrides,
        id: id,
        nodeType:
          nodes.get(id)?.nodeType ??
          overrides?.nodeType ??
          entity?.nodeType ??
          'entity',
        relationshipId:
          overrides?.relationshipId ??
          entity?.relationshipId ??
          nodes.get(id)?.relationshipId,
        children: nodes.get(id)?.children || [],
      });
    }
  };

  const personRelationshipTypes = new Set([
    'director',
    'chairperson',
    'ceo',
    'cfo',
    'coo',
    'cto',
    'president',
    'employee',
    'founder',
    'partner',
    'shareholder',
    'board_member',
  ]);

  const parentFromSourceTypes = new Set(['parent_company']);
  const childFromSourceTypes = new Set([
    'subsidiary',
    'division',
    'branch_office',
    'franchise',
    'director',
    'chairperson',
    'ceo',
    'cfo',
    'coo',
    'cto',
    'president',
    'employee',
    'founder',
    'partner',
    'shareholder',
    'board_member',
  ]);

  const ensureGroupNode = (id: string, name: string) => {
    if (!nodes.has(id)) {
      nodes.set(id, {
        id: id,
        name: name,
        parent_id: rootId,
        nodeType: 'group',
        children: [],
      });
    } else if (!nodes.get(id)?.parent_id) {
      nodes.set(id, {
        ...nodes.get(id),
        id: id,
        name: name,
        parent_id: rootId,
        nodeType: 'group',
        children: nodes.get(id)?.children || [],
      });
    }
  };

  const addToGroup = (
    groupId: string,
    groupName: string,
    entity?: HierarchyEntity | null,
  ) => {
    if (!entity?.id || entity.id === rootId) {
      return;
    }
    ensureGroupNode(groupId, groupName);
    const existing = nodes.get(entity.id);
    addNode(entity, {
      parent_id: existing?.parent_id || groupId,
      relationshipMeta: existing?.relationshipMeta || entity.relationshipMeta,
    });
  };

  hierarchyData?.above?.forEach((entity) => {
    addToGroup('group:parents', 'Parents', entity);
  });
  hierarchyData?.below?.forEach((entity) => {
    addToGroup('group:children', 'Children', entity);
  });

  relationshipEdges?.forEach((edge) => {
    const sourceId = edge.source_entity_id;
    const targetId = edge.target_entity_id;
    if (!sourceId || !targetId) {
      return;
    }
    const isSourceRoot = sourceId === rootId;
    const isTargetRoot = targetId === rootId;
    if (!isSourceRoot && !isTargetRoot) {
      return;
    }
    const relatedId = isSourceRoot ? targetId : sourceId;
    const relatedName = isSourceRoot
      ? edge.target_entity_name
      : edge.source_entity_name;
    if (!relatedId || relatedId === rootId) {
      return;
    }

    const relationshipType = edge.relationship_type;
    const directionalRelationshipType = isSourceRoot
      ? relationshipType
      : invertRelationshipType(relationshipType);
    const isPersonRelation = relationshipType
      ? personRelationshipTypes.has(relationshipType)
      : false;

    const isParentRelationship = relationshipType
      ? parentFromSourceTypes.has(relationshipType)
      : false;
    const isChildRelationship = relationshipType
      ? childFromSourceTypes.has(relationshipType)
      : false;

    const groupId = isParentRelationship
      ? isSourceRoot
        ? 'group:parents'
        : 'group:children'
      : isChildRelationship
      ? isSourceRoot
        ? 'group:children'
        : 'group:parents'
      : isSourceRoot
      ? 'group:children'
      : 'group:parents';
    const groupName = groupId === 'group:parents' ? 'Parents' : 'Children';

    addNode(
      {
        id: relatedId,
        name: relatedName,
        relationshipMeta: directionalRelationshipType,
        relationshipId: edge.id,
        entity_type: isPersonRelation ? 'person' : undefined,
      },
      {
        parent_id: nodes.get(relatedId)?.parent_id || groupId,
      },
    );
    ensureGroupNode(groupId, groupName);
  });

  nodes.forEach((node) => {
    if (node.parent_id && nodes.has(node.parent_id)) {
      nodes.get(node.parent_id)?.children.push(node);
    }
  });

  const root = nodes.get(rootId) || null;

  const groupOrder = new Map([
    ['group:parents', 0],
    ['group:children', 1],
  ]);

  const sortChildren = (node: HierarchyNode) => {
    node.children.sort((a, b) => {
      const aOrder = groupOrder.get(a.id);
      const bOrder = groupOrder.get(b.id);
      if (aOrder !== undefined || bOrder !== undefined) {
        return (aOrder ?? 99) - (bOrder ?? 99);
      }
      return (a.name || '').localeCompare(b.name || '');
    });
    node.children.forEach(sortChildren);
  };

  if (root) {
    sortChildren(root);
  }

  return root;
};

const findPathToNode = (
  node: HierarchyNode | null,
  targetId: string,
): string[] => {
  if (!node) {
    return [];
  }
  if (node.id === targetId) {
    return [node.id];
  }
  for (const child of node.children) {
    const path = findPathToNode(child, targetId);
    if (path.length > 0) {
      return [node.id, ...path];
    }
  }
  return [];
};

const RelationshipHierarchyPanel: React.FC<RelationshipHierarchyPanelProps> = ({
  rootEntity,
  hierarchyData,
  relationshipEdges,
  isLoadingHierarchy,
  hierarchyError,
  selectedEntityId,
  showRelationships,
  onAddRelationship,
  onSelectEntity,
  onRemoveRelationship,
  deletingRelationshipId,
}) => {
  const treeRoot = useMemo(
    () => buildHierarchyTree(rootEntity, hierarchyData, relationshipEdges),
    [rootEntity, hierarchyData, relationshipEdges],
  );

  const selectedId = selectedEntityId || rootEntity?.id || '';
  const pathToSelected = useMemo(
    () => findPathToNode(treeRoot, selectedId),
    [treeRoot, selectedId],
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!treeRoot?.id) {
      return;
    }
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(treeRoot.id);
      treeRoot.children
        .filter((child) => child.nodeType === 'group')
        .forEach((child) => next.add(child.id));
      return next;
    });
  }, [treeRoot]);

  useEffect(() => {
    if (pathToSelected.length === 0) {
      return;
    }
    setExpandedIds((prev) => {
      const next = new Set(prev);
      pathToSelected.forEach((id) => next.add(id));
      return next;
    });
  }, [pathToSelected]);

  const handleToggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderNode = (node: HierarchyNode, level: number) => {
    const isExpanded = expandedIds.has(node.id);
    const hasChildren = node.children.length > 0;
    const isGroup = node.nodeType === 'group';
    const isActive = !isGroup && selectedId === node.id;
    const canDeleteRelationship = Boolean(
      node.relationshipId && !isGroup && node.id !== rootEntity?.id,
    );
    const isDeleting = deletingRelationshipId === node.relationshipId;
    const paddingLeft = 12 + level * 18;

    const meta = getEntityMeta(node);

    return (
      <div key={node.id} className='relationship-tree-node'>
        <div
          className={`relationship-tree-row${isActive ? ' active' : ''}${
            isGroup ? ' group' : ''
          }`}
          style={{ paddingLeft: paddingLeft }}
          onClick={() => {
            if (isGroup) {
              if (hasChildren) {
                handleToggle(node.id);
              }
            } else {
              onSelectEntity(node.id);
            }
          }}
          role='button'
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              if (isGroup) {
                if (hasChildren) {
                  handleToggle(node.id);
                }
              } else {
                onSelectEntity(node.id);
              }
            }
          }}
        >
          <button
            type='button'
            className={`relationship-tree-toggle${
              hasChildren ? '' : ' is-empty'
            }`}
            onClick={(event) => {
              event.stopPropagation();
              if (hasChildren) {
                handleToggle(node.id);
              }
            }}
            aria-label={isExpanded ? 'Collapse node' : 'Expand node'}
          >
            {hasChildren && (
              <KeyboardArrowRightIcon
                fontSize='small'
                style={{
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            )}
          </button>
          <div className='relationship-tree-icon'>{getEntityIcon(node)}</div>
          <div className='relationship-tree-text'>
            <div className='relationship-tree-label'>
              {node.name || 'Unnamed'}
            </div>
            {meta ? <div className='relationship-tree-meta'>{meta}</div> : null}
          </div>
          {canDeleteRelationship ? (
            <button
              type='button'
              className='relationship-tree-delete-btn'
              onClick={(event) => {
                event.stopPropagation();
                onRemoveRelationship(
                  node.relationshipId as string,
                  node.id,
                  node.name,
                );
              }}
              disabled={isDeleting}
              aria-label={`Remove relationship for ${node.name || 'entity'}`}
            >
              {isDeleting ? (
                <CircularProgress size={14} color='inherit' />
              ) : (
                <DeleteOutlineOutlinedIcon fontSize='small' />
              )}
            </button>
          ) : null}
        </div>
        {hasChildren && isExpanded && (
          <div className='relationship-tree-children'>
            {node.children.map((child) => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='relationship-explorer-left'>
      <div className='relationship-card'>
        <div className='relationship-card-header'>
          <div className='relationship-card-title'>Entity Hierarchy</div>
          {showRelationships && (
            <button
              type='button'
              className='relationship-card-action'
              onClick={onAddRelationship}
            >
              <AddOutlinedIcon sx={{ fontSize: 16 }} />
              Add Relationship
            </button>
          )}
        </div>
        <div className='relationship-hierarchy-tree'>
          {!showRelationships ? (
            <div className='relationship-hierarchy-empty'>
              Relationships are only available for entities.
            </div>
          ) : isLoadingHierarchy ? (
            <div className='relationship-hierarchy-empty'>
              Loading hierarchy...
            </div>
          ) : hierarchyError ? (
            <div className='relationship-hierarchy-empty'>
              Error loading hierarchy.
            </div>
          ) : (
            (() => {
              const hasRelationships =
                (relationshipEdges?.length ?? 0) > 0 ||
                (hierarchyData?.above?.length ?? 0) > 0 ||
                (hierarchyData?.below?.length ?? 0) > 0;

              if (!hasRelationships) {
                return (
                  <div className='relationship-hierarchy-empty relationship-hierarchy-empty-center hit-meta'>
                    No relationships created.
                  </div>
                );
              }

              return treeRoot ? (
                renderNode(treeRoot, 0)
              ) : (
                <div className='relationship-hierarchy-empty'>
                  No hierarchy found.
                </div>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default RelationshipHierarchyPanel;
